import React, { useEffect, useState } from 'react';
import { authFetch } from '../lib/authFetch';
import { Button } from '../components/ui/button';
import { Download, Play } from 'lucide-react';
import QuickCheckinModal from '../components/QuickCheckinModal';
import useAllowPrint from '../components/useAllowPrint';

/**
 * SuperAdmin anonymous check-in report (Part 8).
 *
 * Consumes /api/admin/checkin-report and /api/admin/checkin-report/csv.
 * The data is anonymous by design: percentage of check-ins completed,
 * not of users, and cannot be linked to any user.
 *
 * Also hosts the SuperAdmin "Preview check-in" button (Tudor Fix H6,
 * 1 Oct 2026): runs the full check-in flow in a preview mode that
 * records NO anonymous count and does NOT mark a check-in as done, so
 * amber and red screens can be verified safely.
 */
export default function SuperAdminCheckinReport() {
  useAllowPrint();
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${apiBase}/api/admin/checkin-report`);
        if (r.ok) setReport(await r.json());
      } finally { setLoading(false); }
    })();
  }, [apiBase]);

  const downloadCsv = async () => {
    const r = await authFetch(`${apiBase}/api/admin/checkin-report/csv`);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quick_checkin_daily.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500" data-testid="checkin-report-loading">Loading…</div>;
  if (!report) return <div className="p-6 text-sm text-rose-600">Failed to load report.</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="superadmin-checkin-report">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-zinc-900">Check-in results (anonymous)</h1>
          <p className="text-xs text-zinc-500 mt-1">{report.note}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setPreviewOpen(true)}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="checkin-preview-btn"
          >
            <Play className="w-4 h-4 mr-1" /> Preview check-in
          </Button>
          <Button onClick={downloadCsv} variant="outline" data-testid="checkin-report-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export daily totals (CSV)
          </Button>
        </div>
      </div>

      {/* Tudor Fix H6: preview flow — no anonymous count, no schedule mark. */}
      <QuickCheckinModal
        open={previewOpen}
        preview
        onClose={() => setPreviewOpen(false)}
      />

      <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900"
           data-testid="checkin-preview-hint">
        <strong>Preview mode:</strong> running the check-in from here scores answers in your browser only. It does not add to the anonymous count and does not mark a check-in as done.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <WindowCard title="Last 7 days" w={report.last_7_days} testId="checkin-window-7d" />
        <WindowCard title="Last 30 days" w={report.last_30_days} testId="checkin-window-30d" />
        <WindowCard title="All time" w={report.all_time} testId="checkin-window-all" />
      </div>
    </div>
  );
}

function WindowCard({ title, w, testId }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">{title}</div>
      <div className="text-2xl font-bold text-zinc-900 mb-3" data-testid={`${testId}-total`}>{w.total}</div>
      <div className="text-[10px] text-zinc-500 uppercase mb-2">percentage of check-ins completed</div>
      <div className="space-y-1">
        <TierRow color="bg-emerald-500" label="Green" pct={w.green_pct} count={w.green} testId={`${testId}-green`} />
        <TierRow color="bg-amber-500" label="Amber" pct={w.amber_pct} count={w.amber} testId={`${testId}-amber`} />
        <TierRow color="bg-rose-600" label="Red" pct={w.red_pct} count={w.red} testId={`${testId}-red`} />
      </div>
    </div>
  );
}

function TierRow({ color, label, pct, count, testId }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testId}>
      <div className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-zinc-700 w-14">{label}</span>
      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-zinc-600 w-24 text-right">{pct}% ({count})</span>
    </div>
  );
}
